import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-mac',
  category: 'device',
  kicker: 'Mac',
  title: 'How to block porn on a Mac — and why most methods last a week',
  seoTitle: 'How to Block Porn on Mac: 4 Methods, 1 That Holds',
  description: `Screen Time, hosts file, filtering DNS or an app: four ways to block porn on macOS, why each breaks when you are the admin, and the setup that holds.`,
  published: '2026-08-25',
  minutes: 7,
  intro: `On a Mac you are the administrator. That is the whole problem.
          Anything you install, you can uninstall; anything you edit, you can
          edit back. Here are the four usual ways to block porn on macOS,
          honestly rated, and the setup that turns the Mac into one more
          locked device instead of the exception.`,
  next: ['how-to-block-porn-on-iphone', 'how-to-block-porn-on-windows', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>Method 1 — Screen Time on macOS</h2>
      <p>
        Screen Time exists on the Mac too, with the same Content &amp; Privacy
        Restrictions and the same "Limit Adult Websites" switch:
      </p>
      <ol>
        <li><b>System Settings → Screen Time</b>, turn it on.</li>
        <li><b>Content &amp; Privacy → Web Content → Limit Adult Websites</b>.</li>
        <li>Set a Screen Time passcode.</li>
      </ol>
      <p>
        It filters Safari and disables Private Browsing. Then the same
        limits as on iPhone: it's a website filter, not a category filter at
        the network level; it doesn't touch what apps show; and the passcode
        is yours, resettable with your Apple ID. Useful as a first layer.
        Not a lock.
      </p>

      <h2>Method 2 — Editing the hosts file</h2>
      <p>
        The classic. You open Terminal, edit <code>/etc/hosts</code> and
        point a list of domains at <code>0.0.0.0</code>. The sites stop
        resolving. It costs nothing and it feels technical enough to feel
        serious.
      </p>
      <p>
        Two reasons it fails. First, it's a <b>list</b>. You block the sites
        you know; the next one you find is not on the list, and the list is
        never finished. Second, you edit it back with the same command you
        used to write it, and you remember it, because you wrote it. The
        hosts file is a promise to yourself in a text file.
      </p>

      <h2>Method 3 — A filtering DNS resolver</h2>
      <p>
        Better. Instead of a list you write, you point the Mac at a resolver
        that refuses <b>whole categories</b> — pornography, nudity, adult
        themes — for every app and every browser on the machine, private
        windows included. This is the right tool: it's the same mechanism
        SELFCNTRL uses.
      </p>
      <p>
        The weakness is not the filter, it's the setting. <b>System Settings
        → Network → DNS</b> is a text box you can clear in ten seconds, and
        any VPN app replaces the resolver on connect. The category filter is
        right; it's just attached with a sticky note.
      </p>

      <h2>Method 4 — SELFCNTRL for Mac</h2>
      <p>
        The SELFCNTRL app for macOS installs the same DNS category filter, in
        a few minutes, tied to your account. It brings what a bare DNS
        setting can't: the <b>curfews</b> that cut the internet entirely at
        night on a schedule you set once, the <b>pace</b> (cut it dead, or
        four locked days a week that climb to seven), forced SafeSearch on
        Google, YouTube and Bing, and the day counter. Tightening applies at
        once; loosening waits twenty-four hours, and the floor never drops.
      </p>
      <p>
        One honest line, because you'd find out anyway: the lock that
        <em> physically</em> cannot be taken off — the one we put $100
        behind — is the supervised profile on <b>iPhone and iPad</b>, where
        Apple gives us the mechanism to make a profile immovable. macOS
        doesn't offer the same for a personal machine. On the Mac you get
        the same filter and the same curfews, and the block sits inside the
        same account, with the same twenty-four-hour delay to loosen. In
        practice most relapses are on the phone, at night, in bed. That's
        where the immovable lock goes.
      </p>

      <h2>The setup that actually works: every device, one account</h2>
      <p>
        A block on the Mac alone is a block with a phone next to it. The
        setup that holds is the one where every device you own points at the
        same filter:
      </p>
      <ol>
        <li><Link to="/register">Create your space</Link> with one email address.</li>
        <li>Lock the iPhone first — it's the device that's with you at 2am. Plug it into the Mac, run the supervisor, scan the QR code. Ten minutes. See <Link to="/guides/how-to-block-porn-on-iphone">the iPhone guide</Link>.</li>
        <li>Install SELFCNTRL on the Mac. A few minutes.</li>
        <li>Set a night curfew — typically 11pm to 7am — on both.</li>
      </ol>
      <p>
        After that, there's no door left to watch. And nothing to open each
        morning: it just runs.
      </p>

      <h2>What it never does</h2>
      <p>
        No history is read or stored: the filter works on categories, so
        there is nothing to log. No report goes to anyone, no accountability
        partner, no shared dashboard. Your Mac stays yours — the app
        restricts, it doesn't look. Details in the <Link to="/confidentialite">privacy policy</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Does a porn blocker on Mac work in Chrome and Firefox too?',
      a: `A DNS-level filter does: it answers every app on the machine, so
          Safari, Chrome, Firefox, Brave and private windows all get the same
          refusal. Screen Time's "Limit Adult Websites" is Safari-first and
          much weaker elsewhere.`
    },
    {
      q: 'Can I block porn on Mac for free?',
      a: `Yes — Screen Time and a public filtering DNS cost nothing, and both
          are worth turning on today. What you won't get for free is a lock
          you can't undo yourself in ten seconds, or curfews and a pace that
          only ever tightens.`
    },
    {
      q: 'Does it slow the Mac down?',
      a: `No. DNS filtering happens once per domain lookup, in milliseconds,
          on the resolver — nothing scans your traffic or your screen.`
    }
  ]
}

export default guide
